import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { auth } from '../firebase/config';
import { useAuth } from './AuthContext';

const StripeContext = createContext();

// Lazy-load Stripe only when needed (avoids loading on landing page)
let stripePromise = null;
const getStripePromise = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Use local API proxy (Pattern A compliant)
const SECURE_API_URL = process.env.REACT_APP_SECURE_API_URL || 'https://us-central1-booker-1d469.cloudfunctions.net/secureApi';

export const useStripe = () => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};

export const StripeProvider = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const [stripe, setStripe] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define fetchSubscriptionStatus before using it in useEffect
  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      setLoading(true);

      // No admin bypass - fetch actual subscription for all users
      // To get full features, assign an Enterprise subscription via Central

      // Fetch from proxy API (Pattern A compliant)
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const token = await currentUser.getIdToken();
      const appId = 'booking-system';

      const response = await fetch(`${SECURE_API_URL}/proxy/subscriptions/status/${appId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch subscription: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        // Enforce correct free tier limits if plan is 'free'
        // Central API returns -1 (unlimited) for all tiers' appointments/services
        if (result.data.plan === 'free' && result.data.features) {
          result.data.features = {
            ...result.data.features,
            maxAppointments: result.data.features.maxAppointments || -1, // Unlimited
            maxServices: result.data.features.maxServices || -1, // Unlimited
            emailNotifications: true, // Free tier includes email
            smsNotifications: false,
            analytics: false,
            customBranding: false,
            aiChatbot: false,
            aiScheduling: false,
            chatQueries: 0,
            multiLocation: false,
            teamManagement: false,
            leadPipeline: false,
            automation: false,
            advancedCRM: false,
            websiteBuilder: false,
            integrations: false,
            calendarSync: false,
            recurringBookings: false,
            groupBookings: false,
            waitlist: false
          };
        }
        setSubscription(result.data);
      } else {
        throw new Error('Invalid subscription data');
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      setError('Failed to load subscription status');

      // Set default free plan for regular users when function fails
      // IMPORTANT: Include all paid features set to false to prevent downgrades on temporary API errors
      setSubscription({
        hasActiveSubscription: false,
        plan: 'free',
        status: 'inactive',
        features: {
          maxAppointments: -1, // Unlimited
          maxServices: -1, // Unlimited
          emailNotifications: true, // Free tier includes email
          smsNotifications: false,
          analytics: false,
          customBranding: false,
          aiChatbot: false,
          aiScheduling: false,
          chatQueries: 0,
          multiLocation: false,
          teamManagement: false,
          leadPipeline: false,
          automation: false,
          advancedCRM: false,
          websiteBuilder: false,
          integrations: false,
          calendarSync: false,
          recurringBookings: false,
          groupBookings: false,
          waitlist: false
        },
        subscription: null
      });
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  // Load Stripe instance only when user is authenticated
  useEffect(() => {
    if (!currentUser) return;
    const initializeStripe = async () => {
      try {
        const stripeInstance = await getStripePromise();
        setStripe(stripeInstance);
      } catch (err) {
        console.error('Error loading Stripe:', err);
        setError('Failed to load payment system');
      }
    };

    initializeStripe();
  }, [currentUser]);

  // Fetch subscription status when user changes
  useEffect(() => {
    if (currentUser && userProfile) {
      // Only fetch subscription if user is logged in AND profile is loaded
      fetchSubscriptionStatus();
    } else {
      // Set default state for non-authenticated users
      setSubscription(null);
      setLoading(false);
    }
  }, [currentUser, userProfile, fetchSubscriptionStatus]);

  // Listen for platform events from Central Hub (subscription/payment changes)
  useEffect(() => {
    const handleSubscriptionChanged = () => {
      if (currentUser) {
        console.log('[StripeContext] Platform subscription event received, refreshing...');
        fetchSubscriptionStatus();
      }
    };

    window.addEventListener('platform:subscription-changed', handleSubscriptionChanged);
    return () => window.removeEventListener('platform:subscription-changed', handleSubscriptionChanged);
  }, [currentUser, fetchSubscriptionStatus]);

  const createCheckoutSession = async (planOrPriceId, intervalOrNull, currencyOrNull, successUrl, cancelUrl) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in');
      }

      const user = auth.currentUser;
      const token = await currentUser.getIdToken();
      const appId = 'booking-system';

      // Support both new plan-based calls and legacy priceId calls
      let requestData;

      if (typeof planOrPriceId === 'string' && planOrPriceId.startsWith('price_')) {
        // Legacy mode: Direct price ID
        requestData = {
          appId,
          priceId: planOrPriceId,
          successUrl,
          cancelUrl
        };
      } else {
        // New dynamic mode: Plan-based parameters
        // planOrPriceId = plan key (e.g., 'basic', 'professional', 'business')
        // intervalOrNull = interval (e.g., 'monthly', 'yearly')
        // currencyOrNull = currency code (e.g., 'usd', 'cad')
        requestData = {
          appId,
          planId: planOrPriceId,
          interval: intervalOrNull || 'monthly',
          currency: currencyOrNull || 'usd',
          successUrl,
          cancelUrl
        };
      }

      const response = await fetch(`${SECURE_API_URL}/proxy/subscriptions/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to create checkout session: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      throw err;
    }
  };

  const updateSubscription = async (planKey, billingCycle, currency, pricingData) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in');
      }

      const token = await currentUser.getIdToken();
      const appId = 'booking-system';

      const intervalKey = billingCycle === 'yearly' ? 'yearly' : 'monthly';
      const currencyKey = currency.toLowerCase();
      const priceIdKey = `${intervalKey}_${currencyKey}`;
      const newPriceId = pricingData?.prices?.[priceIdKey]?.stripePriceId
        || pricingData?.priceIds?.[intervalKey];

      if (!newPriceId) {
        throw new Error('Could not determine price ID for the selected plan');
      }

      const response = await fetch(`${SECURE_API_URL}/proxy/subscriptions/update/${appId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          newPriceId,
          prorationBehavior: 'create_prorations'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update subscription: ${response.status}`);
      }

      const result = await response.json();

      // Refresh subscription status after update
      await fetchSubscriptionStatus();

      return result.data;
    } catch (err) {
      console.error('Error updating subscription:', err);
      throw err;
    }
  };

  const createPortalSession = async (returnUrl) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in');
      }

      const user = auth.currentUser;
      const token = await currentUser.getIdToken();

      const response = await fetch(`${SECURE_API_URL}/proxy/subscriptions/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ returnUrl })
      });

      if (!response.ok) {
        throw new Error(`Failed to create portal session: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data?.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = result.data.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (err) {
      console.error('Error creating portal session:', err);
      throw err;
    }
  };

  const cancelSubscription = async (subscriptionId) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be logged in');
      }

      const token = await currentUser.getIdToken();

      const response = await fetch(`${SECURE_API_URL}/proxy/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ subscriptionId })
      });

      if (!response.ok) {
        throw new Error(`Failed to cancel subscription: ${response.status}`);
      }

      const result = await response.json();

      // Refresh subscription status
      await fetchSubscriptionStatus();

      return result.data;
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      throw err;
    }
  };

  const reactivateSubscription = async (subscriptionId) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be logged in');
      }

      const token = await currentUser.getIdToken();

      const response = await fetch(`${SECURE_API_URL}/proxy/subscriptions/reactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ subscriptionId })
      });

      if (!response.ok) {
        throw new Error(`Failed to reactivate subscription: ${response.status}`);
      }

      const result = await response.json();

      // Refresh subscription status
      await fetchSubscriptionStatus();

      return result.data;
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      throw err;
    }
  };

  // Check if user has access to a feature
  const hasFeature = (featureName) => {
    if (!subscription?.features) return false;
    return subscription.features[featureName] === true;
  };

  // Check if user is within limits
  const isWithinLimit = (limitName, currentUsage) => {
    if (loading) return true; // Allow during loading state to prevent false negatives
    if (!subscription?.features) return false;

    const limit = subscription.features[limitName];
    if (limit === -1) return true; // unlimited
    return currentUsage < limit;
  };

  // Get feature limit
  const getFeatureLimit = (limitName) => {
    if (!subscription?.features) return 0;
    return subscription.features[limitName];
  };

  // Check if user has active subscription
  const hasActiveSubscription = () => {
    return subscription?.hasActiveSubscription === true;
  };

  // Get plan display name
  const getPlanDisplayName = () => {
    if (!subscription?.plan) return 'Free';
    
    const planNames = {
      free: 'Free',
      enterprise: 'Enterprise',
      basic_monthly: 'Basic (Monthly)',
      basic_yearly: 'Basic (Yearly)',
      pro_monthly: 'Pro (Monthly)',
      pro_yearly: 'Pro (Yearly)',
      enterprise_monthly: 'Enterprise (Monthly)',
      enterprise_yearly: 'Enterprise (Yearly)'
    };
    
    return planNames[subscription.plan] || subscription.plan;
  };

  const value = {
    stripe,
    subscription,
    loading,
    error,
    createCheckoutSession,
    updateSubscription,
    createPortalSession,
    cancelSubscription,
    reactivateSubscription,
    fetchSubscriptionStatus,
    hasFeature,
    isWithinLimit,
    getFeatureLimit,
    hasActiveSubscription,
    getPlanDisplayName
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
};